import { Purchases } from '@revenuecat/purchases-js';
import toast from 'react-hot-toast';

let isConfigured = false;

// Initialize RevenueCat
// Should be called early in your app (e.g. main.tsx)
export const initSubscription = () => {
    const apiKey = import.meta.env.VITE_REVENUECAT_API_KEY;
    if (apiKey) {
        try {
            // Initialize without a specific user ID to allow anonymous session initially.
            Purchases.configure({ apiKey });
            isConfigured = true;
        } catch (error) {
            console.error('Failed to initialize RevenueCat:', error);
        }
    } else {
        console.warn('RevenueCat API key not found. Subscription features will be mock-only.');
    }
};

export interface SubscriptionStatus {
    isActive: boolean;
    entitlements: string[];
    expirationDate: string | null;
}

class SubscriptionService {
    async getStatus(userId: string): Promise<SubscriptionStatus> {
        try {
            if (!import.meta.env.VITE_REVENUECAT_API_KEY || !isConfigured) {
                return { isActive: true, entitlements: ['pro'], expirationDate: null }; // Mock fallback
            }

            const customerInfo = await Purchases.getSharedInstance().getCustomerInfo();
            const entitlement = customerInfo.entitlements.active['pro']; // Assuming 'pro' is your entitlement ID

            return {
                isActive: !!entitlement,
                entitlements: Object.keys(customerInfo.entitlements.active),
                expirationDate: entitlement ? entitlement.expirationDate : null
            };
        } catch (error) {
            console.error('Error fetching subscription status:', error);
            return { isActive: false, entitlements: [], expirationDate: null };
        }
    }

    async logIn(userId: string) {
        if (!import.meta.env.VITE_REVENUECAT_API_KEY || !userId || typeof userId !== 'string' || !isConfigured) {
            console.warn('Skipping RevenueCat login: Missing key, invalid userId, or unconfigured');
            return;
        }
        try {
            await Purchases.getSharedInstance().logIn(userId);
        } catch (error) {
            console.error('RevenueCat login failed:', error);
        }
    }

    async logOut() {
        if (!import.meta.env.VITE_REVENUECAT_API_KEY || !isConfigured) return;
        try {
            await Purchases.getSharedInstance().logOut();
        } catch (error) {
            console.error('RevenueCat logout failed:', error);
        }
    }

    async purchase(packageId: string) {
        try {
            console.log('Initiating purchase for:', packageId);
            // In a real web app with RevenueCat, you might use their Web Billing features 
            // or redirect to a Stripe checkout link. For now, we'll log the attempt.
            toast.error('Web payments need to be configured in RevenueCat dashboard for your specific domain.');
        } catch (error) {
            console.error('Purchase failed:', error);
            throw error;
        }
    }

    getManagementUrl(): string {
        // Return a generic or specific management URL
        return 'https://app.revenuecat.com/settings/billing'; // Example placeholder
    }
}

export const subscriptionService = new SubscriptionService();
